import { AbstractContentLoader } from './abstract-content-loader';
import { FormService } from '../services/form-service';
import { Logger } from '../utils/logger';
import { DomUtils } from '../utils/dom-utils';
export class BlockLoader extends AbstractContentLoader {
    constructor(config) {
        super(config.ajaxUrl, config.nonce, config.postId, config.formId, config.contentId);
        this.config = config;
    }
    /**
     * Initialize the block loader
     */
    initialize() {
        Logger.debug('Initializing block loader', this.config);
        // Register hidden input callback
        this.registerHiddenInputCallback();
        // Set up content refresh functionality
        this.setupContentRefresh();
        // If AJAX form loading is enabled, load the form
        if (this.config.useAjaxFormLoading) {
            this.loadFormViaAjax();
        }
    }
    /**
     * Register hidden input callback for form submission
     */
    registerHiddenInputCallback() {
        FormService.registerHiddenInputCallback({
            type: 'block',
            formId: this.formId,
            postId: this.postId,
            contentId: this.contentId,
        });
    }
    /**
     * Get the content container element
     */
    getContentContainer() {
        return DomUtils.getElementById(DomUtils.getContainerId('block-content', this.contentId));
    }
    /**
     * Get the form container element
     */
    getFormContainer() {
        return DomUtils.getElementById(DomUtils.getFormContainerId(this.contentId));
    }
    /**
     * Get the loading container element
     */
    getLoadingContainer() {
        return DomUtils.getElementById(DomUtils.getLoadingContainerId('block', this.contentId));
    }
    /**
     * Set up content refresh functionality for after form submission
     */
    setupContentRefresh() {
        // Create global function for content refresh
        if (!window.gpsaBlockContentLoaders) {
            window.gpsaBlockContentLoaders = {};
        }
        window.gpsaBlockContentLoaders[this.contentId] = () => {
            this.refreshContent();
        };
        Logger.debug(`Block content refresh function registered for content ID: ${this.contentId}`);
    }
    showLoading(message) {
        if (this.config.settings.fallbackBehavior !== 'hide') {
            super.showLoading(message);
        }
    }
    /**
     * Refresh content after form submission
     */
    async refreshContent() {
        Logger.debug(`Refreshing block content for content ID: ${this.contentId}`);
        // Cache element selections at the start to ensure consistency
        const contentContainer = this.getContentContainer();
        const formContainer = this.getFormContainer();
        try {
            // Hide form and show loading
            // hide only the form as hiding the whole wrapper div causes an issue where the page is
            // scrolled to the top after form submission.
            const form = formContainer.find('form');
            DomUtils.hideElement(form);
            this.showLoading(this.config.settings.loadingMessage);
            // Get updated content
            const response = await this.ajaxService.getBlockContent(this.postId, this.formId, this.contentId.toString());
            Logger.debug(`Block content response received for content ID: ${this.contentId}`, response);
            this.hideLoading();
            if (response.has_access) {
                // User has access, show block content
                const content = typeof response.content === 'string'
                    ? response.content
                    : response.content.innerHTML;
                Logger.debug(`User has access, displaying content for content ID: ${this.contentId}`);
                DomUtils.showElement(contentContainer, content);
                DomUtils.hideElement(formContainer);
            }
            else {
                Logger.debug(`User does not have access, showing form for content ID: ${this.contentId}`);
                // Still no access, show form again
                const content = typeof response.content === 'string'
                    ? response.content
                    : response.content.innerHTML;
                DomUtils.hideElement(contentContainer);
                DomUtils.showElement(formContainer, content);
            }
            // Clear the refresh function to prevent multiple calls
            window.gpsaBlockContentLoaders[this.contentId] = null;
        }
        catch (error) {
            this.handleAjaxError(error, 'block content refresh');
        }
    }
    /**
     * Load form via AJAX (when AJAX form loading is enabled)
     */
    async loadFormViaAjax() {
        try {
            const loadingMessage = this.config.settings.ajaxFormLoadingMessage;
            this.showLoading(loadingMessage);
            const response = await this.ajaxService.getBlockForm(this.postId, this.formId, this.contentId.toString());
            this.hideLoading();
            DomUtils.showElement(this.getFormContainer(), response.content);
            Logger.debug('Block form loaded via AJAX');
        }
        catch (error) {
            this.handleAjaxError(error, 'AJAX form loading');
        }
    }
    /**
     * Static method to create and initialize block loader from config
     * @param config
     */
    static createFromConfig(config) {
        const loader = new BlockLoader(config);
        loader.initialize();
        return loader;
    }
}
